import {
    CLIENT_METRIC_EVENT_LIMITS,
    type ClientCrashFrameTiming,
    type ClientCrashKind,
    type ClientMetricEventBatch,
    type ClientMetricEventInput,
} from '@sorrem/shared';

/** Reports sent per page load at most; past this a crash loop stops reporting itself. */
export const MAX_CRASH_REPORTS_PER_PAGE = 10;

/** How long events wait for others to join their batch before sending. */
export const METRIC_FLUSH_DELAY_MS = 2000;

/** A keepalive request's body may not exceed 64 KiB in flight; batches stay under this. */
const MAX_BATCH_BYTES = 60_000;

/** What went wrong, as a caller reports it. */
export interface ClientCrashInput {
    readonly kind: ClientCrashKind;
    readonly message: string;
    readonly stack?: string;
}

/** The run and frame timing at the moment of a report, when there is a run. */
export interface ClientCrashContext {
    readonly crucibleId?: string;
    readonly frameTiming?: ClientCrashFrameTiming;
}

export interface ClientMetricReporterOptions {
    /** Sends one batch; failures are the sender's to swallow. */
    readonly send: (batch: ClientMetricEventBatch) => void;
    /** The release the page was built from. */
    readonly clientVersion: string;
    readonly getRoute: () => string;
    readonly getContext: () => ClientCrashContext;
    readonly now?: () => Date;
    readonly schedule?: (callback: () => void, delayMs: number) => void;
}

/** `Omit` applied to each member of a union, so the union stays discriminated. */
type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** A client event as a caller records it; the reporter stamps the release. */
export type ClientMetricEventRecord = DistributiveOmit<ClientMetricEventInput, 'clientVersion'>;

/**
 * Batches and sends this page's client metric events (#212, #213). Crash
 * reports are trimmed to the server's limits, repeats of the same failure
 * are dropped, and a page sends a capped number of them; the recent console
 * errors and warnings ride along so each says what led up to it.
 */
export class ClientMetricReporter {
    private readonly options: ClientMetricReporterOptions;
    private readonly now: () => Date;
    private readonly schedule: (callback: () => void, delayMs: number) => void;
    private readonly consoleTail: string[] = [];
    private readonly seen = new Set<string>();
    private pending: ClientMetricEventInput[] = [];
    private reportCount = 0;
    private isFlushScheduled = false;

    constructor(options: ClientMetricReporterOptions) {
        this.options = options;
        this.now = options.now ?? ((): Date => new Date());
        this.schedule = options.schedule ?? ((callback, delayMs): void => {
            setTimeout(callback, delayMs);
        });
    }

    /** Remembers a console error or warning for the next report. */
    recordConsoleLine(line: string): void {
        this.consoleTail.push(truncate(line, CLIENT_METRIC_EVENT_LIMITS.maxConsoleLineLength));
        if (this.consoleTail.length > CLIENT_METRIC_EVENT_LIMITS.maxConsoleLines) {
            this.consoleTail.shift();
        }
    }

    /** The recent console errors and warnings, oldest first. */
    getConsoleTail(): string[] {
        return [...this.consoleTail];
    }

    /** Queues any other client event (a run result shown, a performance sample). */
    record(event: ClientMetricEventRecord): void {
        this.enqueue({ ...event, clientVersion: this.clientVersion() } as ClientMetricEventInput);
    }

    /** Queues a crash report, unless it repeats one already sent or the page is out of reports. */
    reportCrash(input: ClientCrashInput): void {
        const message = input.message.trim() || 'Unknown error';
        const key = `${input.kind}|${message}|${firstStackFrame(input.stack)}`;
        if (this.seen.has(key) || this.reportCount >= MAX_CRASH_REPORTS_PER_PAGE) {
            return;
        }
        this.seen.add(key);
        this.reportCount += 1;

        const context = this.options.getContext();
        this.enqueue({
            eventName: 'client_crash',
            occurredAt: this.now().toISOString(),
            ...(context.crucibleId !== undefined ? { crucibleId: truncate(context.crucibleId, CLIENT_METRIC_EVENT_LIMITS.maxIdLength) } : {}),
            clientVersion: this.clientVersion(),
            payload: {
                kind: input.kind,
                message: truncate(message, CLIENT_METRIC_EVENT_LIMITS.maxMessageLength),
                ...(input.stack ? { stack: truncate(input.stack, CLIENT_METRIC_EVENT_LIMITS.maxStackLength) } : {}),
                route: truncate(this.options.getRoute(), CLIENT_METRIC_EVENT_LIMITS.maxRouteLength),
                consoleTail: [...this.consoleTail],
                ...(context.frameTiming !== undefined ? { frameTiming: context.frameTiming } : {}),
            },
        });
    }

    private enqueue(event: ClientMetricEventInput): void {
        this.pending.push(event);
        if (!this.isFlushScheduled) {
            this.isFlushScheduled = true;
            this.schedule(() => this.flush(), METRIC_FLUSH_DELAY_MS);
        }
    }

    private clientVersion(): string {
        return truncate(this.options.clientVersion, CLIENT_METRIC_EVENT_LIMITS.maxIdLength);
    }

    /** Sends everything queued now (the page is going away, or the batch delay ran out). */
    flush(): void {
        this.isFlushScheduled = false;
        if (this.pending.length === 0) {
            return;
        }

        const events = this.pending;
        this.pending = [];
        for (const batch of splitIntoBatches(events)) {
            this.options.send({ events: batch });
        }
    }
}

/** Splits events into batches within the server's count limit and the keepalive body limit. */
function splitIntoBatches(events: readonly ClientMetricEventInput[]): ClientMetricEventInput[][] {
    const batches: ClientMetricEventInput[][] = [];
    let current: ClientMetricEventInput[] = [];
    let currentBytes = 0;
    for (const event of events) {
        const bytes = JSON.stringify(event).length;
        if (current.length > 0
            && (current.length >= CLIENT_METRIC_EVENT_LIMITS.maxEventsPerBatch || currentBytes + bytes > MAX_BATCH_BYTES)) {
            batches.push(current);
            current = [];
            currentBytes = 0;
        }
        current.push(event);
        currentBytes += bytes;
    }
    if (current.length > 0) {
        batches.push(current);
    }
    return batches;
}

/** The first `at ...` line of a stack, which tells two failures with the same message apart. */
function firstStackFrame(stack: string | undefined): string {
    return stack?.split('\n').find((line) => line.trim().startsWith('at '))?.trim() ?? '';
}

function truncate(value: string, maxLength: number): string {
    return value.length <= maxLength ? value : `${value.slice(0, maxLength - 3)}...`;
}
